import config from '@actr/config';
import { useInject } from '@actr/di/src/hook/use-inject';
import { DateIntervals } from '@actr/specs/src/avia/date-intervals';
import { aviaSearchFormDirections } from '@actr/specs/src/avia/search-form-directions';
import { aviaServiceClasses } from '@actr/specs/src/avia/service-classes';
import type { Locales } from '@actr/specs/src/locales';
import { passengerTypes } from '@actr/specs/src/passenger-types';
import { AviaSearchAdapter } from '@actr/stores/src/services/adapters/avia/search';
import { AppStore } from '@actr/stores/src/stores/stores/common/app';
import { Sizes, ThemeColorValueTypes } from '@actr/style/src/specs/theme-v2';
import { FlexAlight, FlexDirection, FlexJustify, FlexWrapper } from '@actr/ui/src/components/_atoms/flex-wrapper';
import { Space } from '@actr/ui/src/components/_atoms/space';
import { Text } from '@actr/ui/src/components/_atoms/text';
import { ContentBlock } from '@actr/ui/src/components/content-block';
import { Price } from '@actr/ui/src/components/price';
import { formatDate, parseDate } from '@actr/utilities/src/date/format';
import { AkV2Routes } from '@actr/widgets/src/navigation/routes/ak-v2';
import { observer } from 'mobx-react-lite';
import qs from 'qs';
import React, { ReactNode } from 'react';

import { OtherPopularDirections as OtherPopularDirectionsType,
  OtherPopularDirectionsRecommendation } from '@/services/models/avia/other-popular-directions';
import ChannelDecoratedLink from '@/ui/channel-decorated-link';

import * as Styled from './style.css';

/** Неразобранная дата рекомендации не выводится */
function formatRecommendationDate(value: string, locale: Locales): string {
  const parsed = parseDate(value, config.dateTime.serverDateFormatFns);

  return formatDate(parsed, 'd MMMM', locale);
}

interface OtherPopularDirectionsProps {
  data: OtherPopularDirectionsType;
}

const OtherPopularDirections: React.FC<OtherPopularDirectionsProps> = ({ data }) => {
  const { title, list: { data: { to, from } } } = data;
  const { isMobile, locale } = useInject(AppStore);

  function getAviaSearchUrl(fromIata: string, toIata: string, date: string) {
    const searchParams = AviaSearchAdapter.formatSearchParams({
      passengers: [{
        type: passengerTypes.ADULT,
        count: 1
      }],
      direction: aviaSearchFormDirections.ONEWAY,
      serviceClass: aviaServiceClasses.ECONOMY,
      segments: [{
        id: '',
        from: {
          iata: fromIata,
          name: '',
          airport_aggregation_iata: null,
          country: { name: '', iata: '' },
          city: { name: '', iata: '' }
        },
        to: {
          iata: toIata,
          name: '',
          airport_aggregation_iata: null,
          country: { name: '', iata: '' },
          city: { name: '', iata: '' }
        },
        date,
        dateInterval: DateIntervals.EXACTLY,
        returnDate: null,
        returnDateInterval: DateIntervals.EXACTLY
      }],
      searchId: ''
    });
    return `${config.aviakassaURL}${AkV2Routes.AVIA_SEARCH}?${qs.stringify(searchParams)}`;
  }

  function renderDirectionsList(headerTitle: ReactNode, recommendations: OtherPopularDirectionsRecommendation[]) {
    return (
      <div className={Styled.List}>
        <Text className={Styled.Header} font="title.m">
          {headerTitle}
        </Text>
        <div className={Styled.Table}>
          {recommendations.map(recommendation => (
            <ChannelDecoratedLink
              className={Styled.Link}
              key={`${recommendation.from.iata}-${recommendation.to.iata}`}
              href={getAviaSearchUrl(recommendation.from.iata, recommendation.to.iata, recommendation.date)}>
              <div className={Styled.Row}>
                <Text>
                  {recommendation.from.name}
                  {' — '}
                  {recommendation.to.name}
                </Text>
                <FlexWrapper gap={Sizes.X2S} justify={FlexJustify.SPACE_BETWEEN}>
                  <Text>
                    {formatRecommendationDate(recommendation.date, locale)}
                  </Text>
                  <Text colorType={ThemeColorValueTypes.INFO}>
                    <Price amount={recommendation.price} font="paragraph.s.semi" extendColor />
                  </Text>
                </FlexWrapper>
              </div>
            </ChannelDecoratedLink>
          ))}
        </div>
      </div>
    );
  }

  return (
    <ContentBlock>
      <Text tagName="h3" font="title.x2l">
        {title}
      </Text>
      <Text>
        найденные нашими пользователями за последние 28 дней
      </Text>
      <Space size={Sizes.S} />
      <FlexWrapper alight={FlexAlight.START} direction={isMobile ? FlexDirection.COL : FlexDirection.ROW} gap={Sizes.M}>
        {renderDirectionsList(
          <>
            Рейсы в
            {' '}
            {to.title}
            {' '}
            от
            {' '}
            <Price amount={Math.min(...to.recommendations.map(r => r.price))} extendFont />
          </>,
          to.recommendations
        )}
        {renderDirectionsList(
          <>
            Рейсы из
            {' '}
            {from.title}
            {' '}
            от
            {' '}
            <Price amount={Math.min(...from.recommendations.map(r => r.price))} extendFont />
          </>,
          from.recommendations
        )}
      </FlexWrapper>
    </ContentBlock>
  );
};

export default observer(OtherPopularDirections);
